/**
 * Explicit market-code list for the match-page OddsTab.
 *
 * The Sports API's `/v1/odds/events/{id}` endpoint returns every market for
 * the fixture, but the SDK's `MatchOddsFilters` constructor **defaults**
 * `marketTypes` to `["FT_1X2"]` when the caller doesn't pass one, and the
 * SDK's post-fetch filter applies that default — so omitting the list gives
 * you FT_1X2 only. Empty array doesn't override either (the SDK ignores
 * arrays with `length === 0`). Until the SDK grows a "no filter" bypass we
 * have to enumerate every code we want to render.
 *
 * The set below covers every market OddsTab has a section for, expanded
 * across the line thresholds Sportal365 publishes in the wild (verified
 * against a 7-day football sample). Unknown codes fall through the
 * data-layer's `getMarketTypeEnum` as-is with a console warning.
 */

/**
 * Emit `OVER_*` / `ASIAN_HANDICAP_*` line suffixes across a whole range at a
 * given step. Handles the two shape variants Sportal365 sends:
 *
 *   - `"5"` for whole numbers, `"5_5"` for half-lines, `"5_25"` for quarter.
 *
 * Returns just the suffix (e.g. `"2_5"`); caller prefixes it with the
 * market family (`"OVER_GOALS_2_5"`, `"HT_OVER_GOALS_2_5"`, …).
 */
function lineSuffixes(min: number, max: number, step = 0.25): string[] {
    const out: string[] = [];
    for (let n = min; n <= max + 1e-9; n = round(n + step)) {
        out.push(encodeSuffix(n));
    }
    return out;
}

function round(n: number): number {
    return Math.round(n * 100) / 100;
}

/** 2 → "2", 2.5 → "2_5", 2.25 → "2_25", 2.75 → "2_75". */
function encodeSuffix(n: number): string {
    if (Number.isInteger(n)) return String(n);
    const [whole, frac] = n.toString().split(".");
    return `${whole}_${frac}`;
}

const OVER_GOALS_LINES = lineSuffixes(0.5, 10.5);
const HT_OVER_GOALS_LINES = lineSuffixes(0.5, 5, 0.25);
const OVER_CORNERS_LINES = lineSuffixes(4.5, 15.5, 0.5);

/**
 * Asian Handicap comes back with three separate positive/negative encodings:
 * a positive-side line, a lowercase-`neg` variant, and an uppercase-`NEG_`
 * variant. We list all three because different operators use different
 * casings for the same line and the SDK filter is a case-sensitive prefix
 * match after upper-casing.
 */
const AH_LINE_STEPS = lineSuffixes(0, 6, 0.25);
const ASIAN_HANDICAP_POSITIVE = AH_LINE_STEPS.map((s) => `ASIAN_HANDICAP_${s}`);
const ASIAN_HANDICAP_NEG_LOWER = AH_LINE_STEPS.filter((s) => s !== "0").map((s) => `ASIAN_HANDICAP_neg${s}`);
const ASIAN_HANDICAP_NEG_UPPER = AH_LINE_STEPS.filter((s) => s !== "0").map((s) => `ASIAN_HANDICAP_NEG_${s}`);

/**
 * The full list passed to `fetchMatchOdds`. Update this when the OddsTab
 * grows a new section, and mirror the addition in
 * `src/views/match/tabs/OddsTab.tsx`.
 */
export const MATCH_PAGE_ODDS_MARKETS: readonly string[] = [
    // Single-code markets
    "FT_1X2",
    "HT_1X2",
    "DOUBLE_CHANCE",
    "DRAW_NO_BET",
    "HT_FT",
    "BOTH_TEAMS_SCORE",
    "CORRECT_SCORE",
    "TEAM_SCORE_FIRST_GOAL",
    // Line-based families
    ...OVER_GOALS_LINES.map((s) => `OVER_GOALS_${s}`),
    ...HT_OVER_GOALS_LINES.map((s) => `HT_OVER_GOALS_${s}`),
    ...ASIAN_HANDICAP_POSITIVE,
    ...ASIAN_HANDICAP_NEG_LOWER,
    ...ASIAN_HANDICAP_NEG_UPPER,
    ...OVER_CORNERS_LINES.map((s) => `OVER_CORNERS_${s}`),
];
